import { UiOption } from '@/lib/helpers';
import { PolicyModelV1 } from './policy-v1.model';
import { ReactNode } from 'react';
import IntercomButton from '@/components/intercom/intercom-button';
import {
  ControlActionValue,
  ControlCategoryActionMapping,
  getControlActionLevel,
  getSettingOptions,
} from './controls';

export type MetadataControlType = 'non-business-class' | 'non-licensed' | 'enforce-data-privacy';

type MetadataToggleActionValue = 'enabled' | 'disabled';

type MetadataBaseControl = {
  type: MetadataControlType;
  label: string;
  description: ReactNode;
  actionType: 'control' | 'toggle';
};

type MetadataControlsSettings = MetadataBaseControl & {
  actions: ControlActionValue[];
  comingSoonActions?: ControlActionValue[];
  defaultAction: ControlActionValue;
  actionType: 'control';
};

type MetadataEnableSettings = MetadataBaseControl & {
  actions: MetadataToggleActionValue[];
  comingSoonActions?: never;
  defaultAction: boolean;
  actionType: 'toggle';
};

type MetadataControlSettings = MetadataControlsSettings | MetadataEnableSettings;

type MetadataControlEntity = {
  type: MetadataControlType;
  action: ControlActionValue;
};

export const MetadataControlsSettings: MetadataControlSettings[] = [
  {
    type: 'non-licensed',
    label: 'Non-business License Control',
    description: (
      <>
        Choose the action to apply to allowed requests that do not meet the business license
        requirement. Not all services support license classification.{' '}
        <IntercomButton intercomArticle="license-classification" label="Learn more" flatButton />
      </>
    ),
    actions: ['ignore', 'log', 'warn', 'block'],
    actionType: 'control',
    defaultAction: 'ignore',
  },
  {
    type: 'enforce-data-privacy',
    label: 'Attempt to enforce training data protection',
    description:
      'For supported services, attempt to enforce settings which will prevent data from being added to training dataset. When enabled, this setting will be reflected in user event metadata.',
    actions: ['enabled', 'disabled'],
    actionType: 'toggle',
    defaultAction: true,
  },
];

export type MetadataControlConfig = {
  entities: MetadataControlEntity[];
  forceDataPrivacy: boolean;
};

export const getMetadataControlActionOptions = (
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): Map<MetadataControlType, UiOption[]> => {
  const options: Map<MetadataControlType, UiOption[]> = new Map();

  const inheritsFromOrg = policy.inherits();

  MetadataControlsSettings.forEach(({ type, actions, comingSoonActions, defaultAction }) => {
    let settingOptions: UiOption[] = [];

    const allActions = [...actions, ...(comingSoonActions || [])];

    ControlCategoryActionMapping.forEach(({ header, actions: categoryActions }) => {
      if (categoryActions.some((action) => allActions.includes(action))) {
        settingOptions.push({
          label: header,
          value: header.toLowerCase(),
          meta: { header: true },
        });
        for (const action of categoryActions) {
          if (allActions.includes(action)) {
            settingOptions.push(getSettingOptions(action, comingSoonActions?.includes(action)));
          }
        }
      }
    });

    if (inheritsFromOrg) {
      settingOptions = settingOptions.map((setting) => {
        const { value, meta } = setting;

        if (meta?.header) {
          return setting;
        }

        const orgControl = orgPolicy?.metadataControls.entities.find(
          ({ type: orgType }) => orgType === type
        );
        const orgAction = (orgControl?.action || defaultAction) as ControlActionValue;
        const orgActionLevel = getControlActionLevel(orgAction);
        const compLevel = getControlActionLevel(value as ControlActionValue);

        if (orgActionLevel === compLevel) {
          setting.meta = { orgPolicyLevel: true };
        } else if (orgActionLevel < compLevel) {
          setting.disabled = true;
          setting.meta = { blockedByOrgPolicy: true };
        }

        return setting;
      });
    }

    options.set(type, settingOptions);
  });

  return options;
};

export const getMetadataToggleActionOptions = (
  policy: PolicyModelV1,
  orgPolicy?: PolicyModelV1
): UiOption[] => {
  const inheritsFromOrg = policy.inherits();
  const orgPolicyForceDataPrivacy = orgPolicy?.metadataControls.forceDataPrivacy;

  const enabledByDefault = inheritsFromOrg && orgPolicyForceDataPrivacy;

  const options: UiOption[] = [
    {
      label: 'Enabled',
      value: 'enabled',
      meta: {
        orgPolicyLevel: orgPolicyForceDataPrivacy,
        recommended: true,
        default: enabledByDefault,
      },
    },
    {
      label: 'Disabled',
      value: 'disabled',
      disabled: enabledByDefault,
      meta: {
        blockedByOrgPolicy: enabledByDefault,
        orgPolicyLevel: inheritsFromOrg && !orgPolicyForceDataPrivacy,
        default: !enabledByDefault,
      },
    },
  ];

  return options;
};
